import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  Chip,
  Fade,
  IconButton,
  Avatar,
  Divider,
  Paper,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Login,
  Dashboard,
  Security,
  Speed,
  Notifications,
  Store,
  Group,
  Analytics,
  PhoneAndroid,
  Receipt,
  CheckCircle,
  TrendingUp,
  AccessTime,
  LocationOn,
  ArrowForward,
  Star,
  Rocket,
  Shield,
  Bolt,
  BarChart,
  Schedule,
  People,
  PlayArrow,
  KeyboardArrowDown
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';

const Landing = () => {
  const theme = useTheme();
  const { state } = useApp();
  const [visibleSections, setVisibleSections] = useState({});
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Scroll animation trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Testimonial carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Notifications />,
      title: 'Real-Time Alerts',
      description: 'Instant notifications that never let you miss an order. Get audio alerts and push notifications the moment an order arrives.',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      icon: <BarChart />,
      title: 'Smart Analytics',
      description: 'Comprehensive insights into your business performance. Track sales, monitor trends, and make data-driven decisions.',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      icon: <Shield />,
      title: 'Secure Platform',
      description: 'Bank-grade security with OTP authentication. Your data is protected with industry-leading encryption standards.',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      icon: <Bolt />,
      title: 'Lightning Fast',
      description: 'Built for speed and performance. Real-time synchronization ensures your team stays updated instantly.',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    {
      icon: <People />,
      title: 'Team Collaboration',
      description: 'Multi-role support for seamless team management. Assign roles, manage permissions, and work together efficiently.',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    {
      icon: <Schedule />,
      title: '24/7 Reliability',
      description: 'Always-on service with 99.9% uptime guarantee. Your business runs smoothly around the clock.',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    }
  ];

  const stats = [
    { label: 'Active Users', value: '10K+', icon: <People /> },
    { label: 'Orders Processed', value: '1M+', icon: <Receipt /> },
    { label: 'Uptime', value: '99.9%', icon: <TrendingUp /> },
    { label: 'Avg Response', value: '<1s', icon: <Speed /> }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'Restaurant Owner',
      company: 'Spice Garden',
      content: 'This platform transformed our order management. We never miss orders and customer satisfaction has increased by 40%.',
      avatar: '/avatar1.jpg',
      rating: 5
    },
    {
      name: 'Priya Sharma',
      role: 'Store Manager',
      company: 'Fashion Hub',
      content: 'The real-time notifications are a game-changer. Our response time improved dramatically since we started using this.',
      avatar: '/avatar2.jpg',
      rating: 5
    },
    {
      name: 'Ahmed Ali',
      role: 'Business Owner',
      company: 'Tech Solutions',
      content: 'Simple, powerful, and reliable. The analytics help us make better business decisions every day.',
      avatar: '/avatar3.jpg',
      rating: 5
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Quick Setup',
      description: 'Create your account with mobile verification in under 2 minutes',
      icon: <PhoneAndroid />
    },
    {
      number: '02',
      title: 'Configure Store',
      description: 'Add your store details and set up team members with proper roles',
      icon: <Store />
    },
    {
      number: '03',
      title: 'Start Receiving',
      description: 'Begin receiving orders with instant notifications and real-time updates',
      icon: <Notifications />
    }
  ];

  return (
    <Box>
      <AppBar position="static" elevation={0} sx={{ backgroundColor: 'transparent', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold', color: 'text.primary' }}>
            Country Chicken Co.
          </Typography>
          {!state.isAuthenticated && (
            <Button
              component={Link}
              to="/auth"
              variant="contained"
              startIcon={<Login />}
              sx={{
                ml: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold'
              }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h3" align="center" gutterBottom>
          Welcome to Country Chicken Co.
        </Typography>
      </Container>
    </Box>
  );
};

export default Landing; 